import { useEffect, useState, type HTMLAttributes } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Skeleton,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { DataDialog } from "@app/components/atoms";
import { apiGetGroupMachinesList } from "@app/api";
import { setShowAlert } from "@app/store";
import { useDispatch } from "react-redux";

export type GroupMachineListItem = {
  groupId: string;
  machineId: string;
  machineName: string;
  pictureUrl: string;
  modelAndType: string;
  serialNumber: string;
  machineManufacturerName: string;
  machineTypeName: string;
};

type Props = {
  open: boolean;
  groupId: string;
  onClose: () => void;
  onConfirm: (machine: GroupMachineListItem) => void;
};

function normalizeForSearch(value: string): string {
  return value.replace(/\s/g, "").toLowerCase();
}

function getMachineSearchableText(option: GroupMachineListItem): string {
  return normalizeForSearch(
    [
      option.machineName,
      option.machineManufacturerName,
      option.machineTypeName,
      option.modelAndType,
      option.serialNumber,
    ]
      .filter((part) => part && String(part).trim())
      .join(" "),
  );
}

const filterMachinesBase = createFilterOptions<GroupMachineListItem>({
  stringify: getMachineSearchableText,
});

const filterMachines: typeof filterMachinesBase = (options, state) =>
  filterMachinesBase(options, {
    ...state,
    inputValue: normalizeForSearch(state.inputValue),
  });

function getMachineDetailsLine(machine: GroupMachineListItem): string {
  return [
    machine.machineManufacturerName,
    machine.machineTypeName,
    machine.modelAndType,
    machine.serialNumber,
  ]
    .filter((part) => part && String(part).trim())
    .join(" · ");
}

function MachineSelectionDialogSkeleton() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Skeleton
        variant="rounded"
        width="100%"
        height={56}
        sx={{ borderRadius: 1 }}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          p: 1.5,
          bgcolor: "#fafafa",
        }}
      >
        <Skeleton
          variant="rounded"
          width={56}
          height={56}
          sx={{ flexShrink: 0, borderRadius: 1 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton variant="text" width="70%" height={24} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="55%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="40%" height={16} />
        </Box>
      </Box>
    </Box>
  );
}

export function MachineSelectionDialog({
  open,
  groupId,
  onClose,
  onConfirm,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [machines, setMachines] = useState<GroupMachineListItem[]>([]);
  const [selectedMachineId, setSelectedMachineId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !groupId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelectedMachineId("");

    apiGetGroupMachinesList(groupId)
      .then((response) => {
        if (cancelled) return;
        const list = (response?.data?.data ?? []) as GroupMachineListItem[];
        setMachines(list);
        if (list.length === 1) {
          setSelectedMachineId(list[0].machineId);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error("Failed to load machines list:", err);
        const message =
          err instanceof AxiosError
            ? err.response?.data?.error?.message ||
              err.response?.data?.meta?.errorMessage
            : null;
        setError(message || t("errNetwork.content"));
        setMachines([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, groupId, t]);

  const handleConfirm = () => {
    const selected = machines.find((m) => m.machineId === selectedMachineId);
    if (!selected) {
      dispatch(setShowAlert(t("required")));
      return;
    }
    onConfirm(selected);
  };

  const selectedMachine =
    machines.find((m) => m.machineId === selectedMachineId) ?? null;

  const renderMachineOption = (
    props: HTMLAttributes<HTMLLIElement>,
    machine: GroupMachineListItem,
  ) => {
    const { key, ...optionProps } = props as HTMLAttributes<HTMLLIElement> & {
      key: string;
    };

    return (
      <Box
        component="li"
        key={key}
        {...optionProps}
        sx={{ display: "flex", gap: 1.5, py: 1, alignItems: "center" }}
      >
        <Avatar
          src={machine.pictureUrl || undefined}
          alt={machine.machineName}
          variant="rounded"
          sx={{ width: 36, height: 36 }}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {machine.machineName}
          </Typography>
          <Typography
            variant="caption"
            className="txt-gray-3"
            sx={{
              color: "#6B7280",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {getMachineDetailsLine(machine)}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <DataDialog
      open={open}
      onClose={onClose}
      title={t("groupDetailTab.machineTabs")}
      fullWidth
      disableEnforceFocus
      disableBackdropClick
      className="dialog-container"
      dialogContentSx={{ overflow: "visible", pt: 2 }}
      dialogActions={
        <>
          <Button
            className="btn btn-no-border"
            style={{ width: "auto", marginRight: "0px" }}
            onClick={onClose}
          >
            {t("dialogReport.cancel")}
          </Button>
          <Button
            className="btn btn-primary"
            style={{ width: "auto" }}
            onClick={handleConfirm}
            disabled={!selectedMachineId || loading}
          >
            {t("ok")}
          </Button>
        </>
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pt: 1,
          pb: 1,
          minHeight: 120,
        }}
      >
        {loading && <MachineSelectionDialogSkeleton />}

        {!loading && error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        {!loading && !error && machines.length === 0 && (
          <Typography variant="body2" className="txt-gray-1">
            {t("noregmach")}
          </Typography>
        )}

        {!loading && !error && machines.length > 0 && (
          <>
            <Autocomplete
              fullWidth
              className="select-field"
              options={machines}
              value={selectedMachine}
              onChange={(_event, newValue) => {
                setSelectedMachineId(newValue?.machineId ?? "");
              }}
              getOptionLabel={(option) => option.machineName}
              isOptionEqualToValue={(option, value) =>
                option.machineId === value.machineId
              }
              filterOptions={filterMachines}
              disabled={loading}
              noOptionsText={t("noregmach")}
              ListboxProps={{ style: { maxHeight: 360 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="input-field"
                  label={t("groupDetailTab.machineTabs")}
                  placeholder={t("machineInfo.searchmachine")}
                />
              )}
              renderOption={(props, option) => renderMachineOption(props, option)}
            />

            {selectedMachine && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  p: 1.5,
                  bgcolor: "#fafafa",
                }}
              >
                <Avatar
                  src={selectedMachine.pictureUrl || undefined}
                  alt={selectedMachine.machineName}
                  variant="rounded"
                  sx={{ width: 56, height: 56 }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {selectedMachine.machineName}
                  </Typography>
                  <Typography
                    variant="body2"
                    className="txt-gray-3"
                    sx={{ color: "#6B7280" }}
                  >
                    {[
                      selectedMachine.machineManufacturerName,
                      selectedMachine.machineTypeName,
                    ]
                      .filter((part) => part && String(part).trim())
                      .join(" · ")}
                  </Typography>
                  <Typography
                    variant="caption"
                    className="txt-gray-3"
                    sx={{ color: "#6B7280" }}
                  >
                    {[
                      selectedMachine.modelAndType,
                      selectedMachine.serialNumber,
                    ]
                      .filter((part) => part && String(part).trim())
                      .join(" · ")}
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </DataDialog>
  );
}
